import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Paper,
  Popper,
  ClickAwayListener,
  MenuList,
  ListItem,
  Divider,
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import SquareOutlinedIcon from '@mui/icons-material/SquareOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SearchIcon from '@mui/icons-material/Search';
import { REQUISITION_OPTIONS } from '../hooks/useMappingDepartments';

// ─── Hardcoded options (swap for API calls when ready) ───────────────────────
const L3_OPTIONS = ['Operations', 'Finance', 'Technology', 'Sales', 'HR', 'Logistics', 'Customer Success'];
const L2_OPTIONS = ['North Region', 'South Region', 'East Region', 'West Region', 'Central', 'Corporate'];
const L1_OPTIONS = ['Audit', 'Budget', 'Compliance', 'Forecast', 'General'];

// These will eventually come from saved departments data
const SHARE_TO_OPTIONS = [
  '10 – Assembly',
  '20 – Packaging',
  '30 – QA',
  '40 – Dispatch',
  '50 – Procurement',
  '60 – Admin',
  '70 – IT',
  '80 – Finance',
];

// Split=Yes departments where shareTo was left blank.
// In production: filter saved departments where splitDept=true AND reportingShareTo=[]
// Shape: [{ deptNumber: "91", deptName: "Maintenance" }, ...]
const SPLIT_BLANK_SHARE_TO_DEPTS = [
  { deptNumber: '91', deptName: 'Maintenance' },
  { deptNumber: '92', deptName: 'Security' },
  { deptNumber: '93', deptName: 'Facilities' },
  { deptNumber: '94', deptName: 'Legal' },
  { deptNumber: '95', deptName: 'Marketing' },
];

const EMPTY_FORM = {
  splitDept: null, // true | false
  deptNumberDoNotEdit: '',
  deptNumberDeptNameForReporting: '',
  businessUnitLevel3: '',
  departmentGroupLevel2: '',
  departmentChecksLevel1: '',
  reportingShareTo: '', // array, split=Yes only
  reportingIncludeDept: [], // array, split=No only
  reportingIncludeYesOrNo: 'No',
  requisitionType: '',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    borderRadius: '10px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#0f172a' },
  },
  '& .MuiInputLabel-root': { fontSize: '13px' },
};

const labelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  display: 'block',
  mb: 0.75,
};

// ─── Multi-select with search (flat list) ─────────────────────────────────────
// Used for Split=Yes "Share to" — flat list of SHARE_TO_OPTIONS categories
// function MultiSelectSearch({ label, options, value, onChange, placeholder = 'Search…', hint }) {
//   const [open, setOpen] = useState(false);
//   const [search, setSearch] = useState('');
//   const anchorRef = useRef(null);

//   const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

//   const toggle = opt => {
//     onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
//   };

//   const removeTag = (opt, e) => {
//     e.stopPropagation();
//     onChange(value.filter(v => v !== opt));
//   };

//   return (
//     <ClickAwayListener
//       onClickAway={() => {
//         setOpen(false);
//         setSearch('');
//       }}>
//       <Box>
//         <Typography sx={labelSx}>{label}</Typography>
//         <Box
//           ref={anchorRef}
//           onClick={() => setOpen(o => !o)}
//           sx={{
//             minHeight: '36px',
//             border: '1px solid',
//             borderColor: open ? '#0f172a' : '#e2e8f0',
//             borderRadius: '10px',
//             bgcolor: '#fff',
//             px: 1,
//             py: 0.5,
//             cursor: 'pointer',
//             display: 'flex',
//             flexWrap: 'wrap',
//             gap: 0.5,
//             alignItems: 'center',
//             '&:hover': { borderColor: '#cbd5e1' },
//             transition: 'border-color .15s',
//           }}>
//           {value.length === 0 && (
//             <Typography sx={{ fontSize: '13px', color: '#94a3b8', lineHeight: '26px' }}>{placeholder}</Typography>
//           )}
//           {value.map(v => (
//             <Chip
//               key={v}
//               label={v}
//               size="small"
//               onDelete={e => removeTag(v, e)}
//               sx={{
//                 fontSize: '11px',
//                 height: '22px',
//                 bgcolor: '#f1f5f9',
//                 color: '#334155',
//                 borderRadius: '5px',
//                 '& .MuiChip-deleteIcon': { fontSize: '14px', color: '#94a3b8' },
//               }}
//             />
//           ))}
//           <Box sx={{ ml: 'auto', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
//             <SearchIcon sx={{ fontSize: '16px' }} />
//           </Box>
//         </Box>

//         {hint && <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>{hint}</Typography>}

//         <Popper
//           open={open}
//           anchorEl={anchorRef.current}
//           placement="bottom-start"
//           style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}>
//           <Paper elevation={3} sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', mt: 0.5 }}>
//             <Box sx={{ p: 1, borderBottom: '1px solid #f1f5f9' }}>
//               <TextField
//                 autoFocus
//                 size="small"
//                 fullWidth
//                 placeholder="Search…"
//                 value={search}
//                 onChange={e => setSearch(e.target.value)}
//                 onClick={e => e.stopPropagation()}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <SearchIcon sx={{ fontSize: '15px', color: '#94a3b8' }} />
//                     </InputAdornment>
//                   ),
//                 }}
//                 sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], fontSize: '12px' } }}
//               />
//             </Box>
//             <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//               {filtered.length === 0 && (
//                 <Typography sx={{ fontSize: '12px', color: '#94a3b8', p: 1.5, textAlign: 'center' }}>No results</Typography>
//               )}
//               {filtered.map(opt => {
//                 const checked = value.includes(opt);
//                 return (
//                   <Box
//                     key={opt}
//                     onClick={() => toggle(opt)}
//                     sx={{
//                       display: 'flex',
//                       alignItems: 'center',
//                       gap: 1,
//                       px: 1.5,
//                       py: 0.75,
//                       cursor: 'pointer',
//                       bgcolor: checked ? 'rgba(12,147,68,0.05)' : 'transparent',
//                       '&:hover': { bgcolor: checked ? 'rgba(12,147,68,0.08)' : '#f8fafc' },
//                     }}>
//                     {checked ? (
//                       <CheckBoxIcon sx={{ fontSize: '16px', color: '#0C9344' }} />
//                     ) : (
//                       <CheckBoxOutlineBlankIcon sx={{ fontSize: '16px', color: '#cbd5e1' }} />
//                     )}
//                     <Typography
//                       sx={{ fontSize: '13px', color: checked ? '#0C9344' : '#334155', fontWeight: checked ? 500 : 400 }}>
//                       {opt}
//                     </Typography>
//                   </Box>
//                 );
//               })}
//             </Box>
//           </Paper>
//         </Popper>
//       </Box>
//     </ClickAwayListener>
//   );
// }

function SingleSelectSearch({ label, options, value, onChange, placeholder = 'Search…', hint }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  const select = opt => {
    onChange(opt);
    setOpen(false);
    setSearch('');
  };

  const clear = e => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
        setSearch('');
      }}>
      <Box>
        <Typography sx={labelSx}>{label}</Typography>
        <Box
          ref={anchorRef}
          onClick={() => setOpen(o => !o)}
          sx={{
            minHeight: '36px',
            border: '1px solid',
            borderColor: open ? '#0f172a' : '#e2e8f0',
            borderRadius: '10px',
            bgcolor: '#fff',
            px: 1.5,
            py: 0.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { borderColor: '#cbd5e1' },
            transition: 'border-color .15s',
          }}>
          {value ? (
            <Chip
              label={value}
              size="small"
              onDelete={clear}
              sx={{
                fontSize: '11px',
                height: '22px',
                bgcolor: '#f1f5f9',
                color: '#334155',
                borderRadius: '5px',
                '& .MuiChip-deleteIcon': { fontSize: '14px', color: '#94a3b8' },
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '13px', color: '#94a3b8', lineHeight: '26px' }}>{placeholder}</Typography>
          )}
          <Box sx={{ ml: 'auto', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
            <SearchIcon sx={{ fontSize: '16px' }} />
          </Box>
        </Box>

        {hint && <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>{hint}</Typography>}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}>
          <Paper elevation={3} sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', mt: 0.5 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid #f1f5f9' }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '15px', color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], fontSize: '12px' } }}
              />
            </Box>
            <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filtered.length === 0 && (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', p: 1.5, textAlign: 'center' }}>No results</Typography>
              )}
              {filtered.map(opt => {
                const checked = value === opt;
                return (
                  <Box
                    key={opt}
                    onClick={() => select(opt)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      bgcolor: checked ? 'rgba(12,147,68,0.05)' : 'transparent',
                      '&:hover': { bgcolor: checked ? 'rgba(12,147,68,0.08)' : '#f8fafc' },
                    }}>
                    <Typography
                      sx={{ fontSize: '13px', color: checked ? '#0C9344' : '#334155', fontWeight: checked ? 500 : 400 }}>
                      {opt}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

// ─── Grouped multi-select: categories + dept numbers ─────────────────────────
// Used for Split=No "Include dept"
// categories: string[]           → SHARE_TO_OPTIONS
// depts: { deptNumber, deptName }[] → split=Yes depts where shareTo was blank
function GroupedMultiSelectSearch({ label, categories, depts, value, onChange, hint }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);

  const q = search.toLowerCase();

  const filteredCats = categories.filter(c => c.toLowerCase().includes(q));
  const filteredDepts = depts.filter(d => d.deptNumber.includes(q) || d.deptName.toLowerCase().includes(q));

  const deptKey = d => String(d.deptNumber);

  const getDeptLabel = key => {
    const dept = depts.find(d => String(d.deptNumber) === key);
    return dept ? `${dept.deptName}` : key; // categories pass through as-is
  };

  const toggle = key => {
    onChange(value.includes(key) ? value.filter(v => v !== key) : [...value, key]);
  };

  const removeTag = (key, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== key));
  };

  const SelectRow = ({ itemKey, itemLabel }) => {
    const checked = value.includes(itemKey);
    return (
      <Box
        onClick={() => toggle(itemKey)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          bgcolor: checked ? 'rgba(12,147,68,0.05)' : 'transparent',
          '&:hover': { bgcolor: checked ? 'rgba(12,147,68,0.08)' : '#f8fafc' },
        }}>
        {checked ? (
          <CheckBoxIcon sx={{ fontSize: '16px', color: '#0C9344' }} />
        ) : (
          <CheckBoxOutlineBlankIcon sx={{ fontSize: '16px', color: '#cbd5e1' }} />
        )}
        <Typography sx={{ fontSize: '13px', color: checked ? '#0C9344' : '#334155', fontWeight: checked ? 500 : 400 }}>
          {itemLabel}
        </Typography>
      </Box>
    );
  };

  const SectionLabel = ({ text }) => (
    <Typography
      sx={{
        fontSize: '10px',
        fontWeight: 600,
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        px: 1.5,
        pt: 1,
        pb: 0.25,
      }}>
      {text}
    </Typography>
  );

  const showCatSection = filteredCats.length > 0;
  const showDeptSection = filteredDepts.length > 0;

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
        setSearch('');
      }}>
      <Box>
        <Typography sx={labelSx}>{label}</Typography>
        <Box
          ref={anchorRef}
          onClick={() => setOpen(o => !o)}
          sx={{
            minHeight: '36px',
            border: '1px solid',
            borderColor: open ? '#0f172a' : '#e2e8f0',
            borderRadius: '10px',
            bgcolor: '#fff',
            px: 1,
            py: 0.5,
            cursor: 'pointer',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            '&:hover': { borderColor: '#cbd5e1' },
            transition: 'border-color .15s',
          }}>
          {value.length === 0 && (
            <Typography sx={{ fontSize: '13px', color: '#94a3b8', lineHeight: '26px' }}>
              Search by category or dept number…
            </Typography>
          )}
          {value.map(v => (
            <Chip
              key={v}
              label={getDeptLabel(v)}
              size="small"
              onDelete={e => removeTag(v, e)}
              sx={{
                fontSize: '11px',
                height: '22px',
                bgcolor: '#f1f5f9',
                color: '#334155',
                borderRadius: '5px',
                '& .MuiChip-deleteIcon': { fontSize: '14px', color: '#94a3b8' },
              }}
            />
          ))}
          <Box sx={{ ml: 'auto', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
            <SearchIcon sx={{ fontSize: '16px' }} />
          </Box>
        </Box>

        {hint && <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>{hint}</Typography>}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}>
          <Paper elevation={3} sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', mt: 0.5 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid #f1f5f9' }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder="Search category or dept no…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '15px', color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], fontSize: '12px' } }}
              />
            </Box>

            <Box sx={{ maxHeight: '240px', overflowY: 'auto' }}>
              {!showCatSection && !showDeptSection && (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', p: 1.5, textAlign: 'center' }}>No results</Typography>
              )}

              {/* Categories section */}
              {showCatSection && (
                <>
                  <SectionLabel text="Categories" />
                  {filteredCats.map(cat => (
                    <SelectRow key={cat} itemKey={cat} itemLabel={cat} />
                  ))}
                </>
              )}

              {/* Divider between sections */}
              {showCatSection && showDeptSection && <Box sx={{ mx: 1.5, my: 0.5, borderTop: '1px solid #f1f5f9' }} />}

              {/* Dept numbers section */}
              {showDeptSection && (
                <>
                  <SectionLabel text="Dept numbers (split, no share-to)" />
                  {filteredDepts.map(d => (
                    <SelectRow key={deptKey(d)} itemKey={deptKey(d)} itemLabel={`${d.deptNumber} – ${d.deptName}`} />
                  ))}
                </>
              )}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

const toArray = (val, delimiter) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  return val
    .split(delimiter)
    .map(s => s.trim())
    .filter(Boolean);
};

const buildInitialForm = data => {
  if (!data) return EMPTY_FORM;
  return {
    ...data,
    reportingIncludeDept: toArray(data.reportingIncludeDept, ','),
  };
};

// ─── Main form ────────────────────────────────────────────────────────────────
export function DepartmentForm({ initial = null, onSubmit, onCancel, loading, departments = [] }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => buildInitialForm(initial));
  const [error, setError] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const isSplit = form.splitDept === true;
  const isNoSplit = form.splitDept === false;

  // Keep dept name prefix in sync with dept number
  useEffect(() => {
    if (!form.deptNumberDoNotEdit) return;

    const prefix = `${form.deptNumberDoNotEdit} - `;
    const current = form.deptNumberDeptNameForReporting;

    // Strip any existing prefix (everything before first " - ") then re-prefix
    const namePart = current.includes(' - ')
      ? current
          .split(' - ')
          .slice(1)
          .join(' - ')
      : current;

    set('deptNumberDeptNameForReporting', `${prefix}${namePart}`);
  }, [form.deptNumberDoNotEdit]);

  // ─── Derived dropdown options from real departments data ───────────────────

  const l3Options = useMemo(() => [...new Set(departments.map(d => d.businessUnitLevel3).filter(Boolean))].sort(), [
    departments,
  ]);

  const l2Options = useMemo(() => {
    if (!form.businessUnitLevel3) return [];
    return [
      ...new Set(
        departments
          .filter(d => d.businessUnitLevel3 === form.businessUnitLevel3)
          .map(d => d.departmentGroupLevel2)
          .filter(Boolean),
      ),
    ].sort();
  }, [departments, form.businessUnitLevel3]);

  const l1Options = useMemo(() => {
    if (!form.businessUnitLevel3) return [];
    return [
      ...new Set(
        departments
          .filter(d => d.businessUnitLevel3 === form.businessUnitLevel3)
          .map(d => d.departmentChecksLevel1)
          .filter(Boolean),
      ),
    ].sort();
  }, [departments, form.businessUnitLevel3]);

  // ─── When L3 changes, reset L2 and L1 ─────────────────────────────────────
  const handleL3Change = val => {
    const matched = departments.filter(d => d.businessUnitLevel3 === val);
    setForm(f => ({
      ...f,
      businessUnitLevel3: val,
      departmentGroupLevel2: matched[0]?.departmentGroupLevel2 || '',
      departmentChecksLevel1: matched[0]?.departmentChecksLevel1 || '',
    }));
  };

  // ─── Share-to options: unique reportingShareTo values from real data ───────
  const shareToOptions = useMemo(() => {
    const all = departments.flatMap(d => {
      if (!d.reportingShareTo) return [];
      return d.reportingShareTo
        .split('|')
        .map(s => s.trim())
        .filter(Boolean);
    });
    return [...new Set(all)].sort();
  }, [departments]);

  // ─── Split=Yes depts where shareTo is blank ────────────────────────────────
  const splitBlankShareToDepts = useMemo(
    () =>
      departments
        .filter(
          d => d.reportingDepartmentToSplitYesNo === 'Yes' && (!d.reportingShareTo || d.reportingShareTo.trim() === ''),
        )
        .map(d => ({
          deptNumber: String(d.deptNumberDoNotEdit),
          deptName: d.deptNumberDeptNameForReporting?.split('–')[1]?.trim() || d.deptNumberDeptNameForReporting || '',
        })),
    [departments],
  );

  // ─── Requisition chips ─────────────────────────────────────────────────────
  const selectedReq = form.requisitionType
    ? form.requisitionType
        .split('|')
        .map(v => v.trim())
        .filter(Boolean)
    : [];

  const toggleReq = opt => {
    const next = selectedReq.includes(opt) ? selectedReq.filter(s => s !== opt) : [...selectedReq, opt];
    set('requisitionType', next.join(' | '));
  };

  const handleSubmit = async () => {
    setError(null);
    if (form.splitDept === null) {
      setError('Please select whether this is a split department.');
      return;
    }
    if (!form.deptNumberDoNotEdit) {
      setError('Department number is required.');
      return;
    }
    const payload = {
      ...form,
      reportingIncludeDept: form.reportingIncludeDept.join(','),
    };
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      {/* ── Step 1: Split toggle ── */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={labelSx}>Is this a split department?</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { val: true, label: 'Yes, split', icon: <CallSplitIcon sx={{ fontSize: '16px' }} /> },
            { val: false, label: 'No, standalone', icon: <SquareOutlinedIcon sx={{ fontSize: '16px' }} /> },
          ].map(({ val, label, icon }) => {
            const active = form.splitDept === val;
            const color = val ? '#065f46' : '#1e40af';
            const bg = val ? 'rgba(6,95,70,0.07)' : 'rgba(30,64,175,0.07)';
            const border = val ? 'rgba(6,95,70,0.3)' : 'rgba(30,64,175,0.3)';
            return (
              <Button
                key={String(val)}
                variant="outlined"
                size="small"
                startIcon={icon}
                onClick={() => set('splitDept', val)}
                sx={{
                  flex: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: active ? 600 : 400,
                  color: active ? color : '#64748b',
                  bgcolor: active ? bg : '#fff',
                  borderColor: active ? border : '#e2e8f0',
                  borderWidth: active ? '1.5px' : '1px',
                  '&:hover': { bgcolor: active ? bg : '#f8fafc', borderColor: active ? border : '#cbd5e1' },
                }}>
                {label}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* ── Step 2: Fields ── */}
      {form.splitDept !== null && (
        <Grid container spacing={2}>
          {/* Dept number */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Department number"
              type="number"
              size="small"
              fullWidth
              value={form.deptNumberDoNotEdit}
              disabled={isEdit}
              onChange={e => set('deptNumberDoNotEdit', e.target.value)}
              placeholder="e.g. 997"
              helperText="Should match your Sage department number"
              FormHelperTextProps={{ sx: { fontSize: '11px', color: '#94a3b8' } }}
              sx={fieldSx}
            />
          </Grid>

          {/* Dept name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Dept name for reporting"
              size="small"
              fullWidth
              value={form.deptNumberDeptNameForReporting}
              onChange={e => {
                const prefix = `${form.deptNumberDoNotEdit} - `;
                const val = e.target.value;
                // If user tries to delete into the prefix, restore it
                if (!val.startsWith(prefix)) {
                  set('deptNumberDeptNameForReporting', prefix);
                } else {
                  set('deptNumberDeptNameForReporting', val);
                }
              }}
              placeholder={form.deptNumberDoNotEdit ? `${form.deptNumberDoNotEdit} - Dept name` : 'Enter dept number first'}
              disabled={!form.deptNumberDoNotEdit}
              sx={fieldSx}
            />
          </Grid>

          {/* Level 3 — drives L2 + L1 */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Business / dept (level 3)"
              size="small"
              fullWidth
              value={form.businessUnitLevel3}
              onChange={e => handleL3Change(e.target.value)}
              sx={fieldSx}>
              <MenuItem value="">— select —</MenuItem>
              {l3Options.map(o => (
                <MenuItem key={o} value={o} sx={{ fontSize: '13px' }}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Level 2 — filtered by L3 */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Department group (level 2)"
              size="small"
              fullWidth
              value={form.departmentGroupLevel2}
              onChange={e => set('departmentGroupLevel2', e.target.value)}
              disabled={!form.businessUnitLevel3}
              sx={fieldSx}>
              <MenuItem value="">— select —</MenuItem>
              {l2Options.map(o => (
                <MenuItem key={o} value={o} sx={{ fontSize: '13px' }}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Level 1 — filtered by L3 */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Department checks (level 1)"
              size="small"
              fullWidth
              value={form.departmentChecksLevel1}
              onChange={e => set('departmentChecksLevel1', e.target.value)}
              disabled={!form.businessUnitLevel3}
              sx={fieldSx}>
              <MenuItem value="">— select —</MenuItem>
              {l1Options.map(o => (
                <MenuItem key={o} value={o} sx={{ fontSize: '13px' }}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Include in reporting */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Include in reporting"
              size="small"
              fullWidth
              value={form.reportingIncludeYesOrNo}
              onChange={e => set('reportingIncludeYesOrNo', e.target.value)}
              sx={fieldSx}>
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </TextField>
          </Grid>

          {/* Split=Yes: Share to */}
          {isSplit && (
            <Grid item xs={12}>
              <SingleSelectSearch
                label="Share to (optional)"
                options={shareToOptions}
                value={form.reportingShareTo}
                onChange={val => set('reportingShareTo', val)}
                placeholder="Select department to share to…"
              />
            </Grid>
          )}

          {/* Split=No: Include dept */}
          {isNoSplit && (
            <Grid item xs={12}>
              <GroupedMultiSelectSearch
                label="Include dept"
                categories={shareToOptions}
                depts={splitBlankShareToDepts}
                value={form.reportingIncludeDept}
                onChange={val => set('reportingIncludeDept', val)}
                hint="Select categories or search for split departments with no share-to assigned"
              />
            </Grid>
          )}

          {/* Requisition type chips */}
          <Grid item xs={12}>
            <Typography sx={labelSx}>Requisition type</Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 1.5,
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                bgcolor: '#fafafa',
              }}>
              {REQUISITION_OPTIONS.map(opt => {
                const checked = selectedReq.includes(opt);
                return (
                  <Chip
                    key={opt}
                    label={opt}
                    size="small"
                    onClick={() => toggleReq(opt)}
                    icon={
                      checked ? (
                        <CheckBoxIcon sx={{ fontSize: '15px !important', color: '#0C9344 !important' }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon sx={{ fontSize: '15px !important', color: '#64748b !important' }} />
                      )
                    }
                    sx={{
                      fontSize: '12px',
                      fontWeight: checked ? 600 : 400,
                      cursor: 'pointer',
                      borderRadius: '6px',
                      bgcolor: checked ? 'rgba(12, 147, 68, 0.08)' : '#fff',
                      color: checked ? '#0C9344' : '#475569',
                      border: '1px solid',
                      borderColor: checked ? 'rgba(12, 147, 68, 0.3)' : '#e2e8f0',
                      '&:hover': { bgcolor: checked ? 'rgba(12, 147, 68, 0.12)' : '#f8fafc' },
                    }}
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Error */}
      {error && <Typography sx={{ color: 'error.main', fontSize: 13, mt: 1.5 }}>{error}</Typography>}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          disabled={loading}
          sx={{ borderRadius: '8px', textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={loading || form.splitDept === null}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#0C9344', '&:hover': { bgcolor: '#0a7d3a' } }}>
          {loading ? 'Saving…' : isEdit ? 'Save changes' : isSplit ? 'Add split department' : 'Add department'}
        </Button>
      </Box>
    </Box>
  );
}
